import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export interface IPasswordUpdate {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface IUser {
  id: string;
  name: string;
  userName: string;
  displayName?: string;
  email: string;
  phoneNumber?: string | null;
  password?: string;
  groups: {
    uniqueKey: string;
    name: string;
  }[];
}

const key = "me";
const endpoint = "/api/me";

export default function useProfile() {
  const toast = useToast({
    status: "success",
    duration: 2000,
    isClosable: true,
    position: "top",
  });

  const queryClient = useQueryClient();

  const user = useQuery<IUser, Error>({
    queryKey: [key],
    queryFn: async () => {
      const { data } = await axios.get<IUser>(endpoint);
      return data;
    },
  });

  const update = useMutation<IUser, Error, any, any>({
    mutationFn: async (user: IUser) => {
      const { data } = await axios.put(endpoint, user);
      return data;
    },
    onMutate: () => {
      toast.close(`update-toast-me`);
    },
    onSuccess: (updatedEntity) => {
      !toast.isActive(`update-toast-me`) &&
        toast({ title: "Profile updated", id: `update-toast-me` });

      queryClient.setQueryData([key], updatedEntity);
    },
    onError: (error) => {
      toast({
        title: error?.message ?? "Error",
        status: "error",
        id: `update-toast-me`,
      });
    },
  });

  const changePassword = useMutation<IPasswordUpdate, Error, any, any>({
    mutationFn: async (params: IPasswordUpdate) => {
      const { data } = await axios.put(`api/ChangePassword`, params);
      return data;
    },
    onMutate: () => {
      toast.close(`update-toast-me`);
    },
    onSuccess: () => {
      !toast.isActive(`update-toast-me}`) &&
        toast({
          title: "Password updated",
          id: `update-toast-me`,
        });
    },
    onError: (error) => {
      toast({
        title: error?.message ?? "Error",
        status: "error",
        id: `update-toast-me`,
      });
    },
  });

  return { user, update, changePassword };
}

export const useRequestOTP = () =>
  useMutation<any, Error, any, any>({
    mutationFn: async (userName: string) => {
      const { data: response } = await axios.post("/api/account/request", {
        userName,
      });
      return response;
    },
  });

export const useOTPReset = () =>
  useMutation<any, Error, any, any>({
    mutationFn: async ({ userName, otp, password }) => {
      const { data: response } = await axios.post("/api/account/reset", {
        userName,
        otp,
        password,
      });
      return response;
    },
  });
